import React, { useState } from 'react';
import { CheckCircle2, Send, Star, MessageSquare, ThumbsUp, HelpCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScreenInit } from '../useScreenInit';
export function ReviewForm() {
  const screenInit = useScreenInit();
  const [submitted, setSubmitted] = useState(screenInit?.submitted || false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    email: '',
    usefulness: '',
    valuablePart: '',
    clarity: '',
    recommend: '',
    recommendQuote: '',
    improvement: '',
    testimonialConsent: ''
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // PASTE YOUR GOOGLE APPS SCRIPT URL HERE
    // Once you deploy your script as a Web App, replace the string below with your URL
    const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzfZM7-UrbHHs8KdKLl4pkrVhcNGF24rg_bfUmYxYG4ZW1y0qxgluBsMaWw9oSnf3X1eA/exec';
    try {
      const response = await fetch(SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      console.log('Form submitted successfully');
      // Send to Slack
      try {
        const slackMessage = `*New Workshop Feedback* 🎉\n*Name:* ${formData.name}\n*Company:* ${formData.companyName}\n*Email:* ${formData.email}\n*Usefulness:* ${formData.usefulness}\n*Most Valuable Part:* ${formData.valuablePart}\n*Clarity:* ${formData.clarity}\n*Would Recommend:* ${formData.recommend}\n*What They'd Tell Others:* ${formData.recommendQuote}\n*Improvements:* ${formData.improvement}\n*Testimonial Consent:* ${formData.testimonialConsent}`;
        await fetch('https://hooks.slack.com/services/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', {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            text: slackMessage
          })
        });
      } catch (slackError) {
        console.error('Error sending to Slack:', slackError);
      }
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  if (submitted) {
    return <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="max-w-2xl mx-auto text-center py-20 px-6" data-id="element-2">
        <div className="bg-white p-12 rounded-3xl shadow-xl border border-orange-100" data-id="element-3">
          <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-4">
            <CheckCircle2 className="w-10 h-10 text-orange-600" data-id="element-5" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-6">Thank you!</h2>
          <p className="text-lg text-gray-600 mb-8" data-id="element-7">
            Your feedback has been received. It helps immensely in making these
            workshops better for everyone.
          </p>
          <div className="flex flex-col items-center gap-6" data-id="element-8">
            <a href="https://tidycal.com/avy/clarity90" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-orange-600 text-white font-bold rounded-xl shadow-lg hover:bg-orange-700 hover:shadow-orange-200 transition-all active:scale-[0.98]" data-id="element-9">
              Book a 90 min Clarity Call with Avy
            </a>
            <button onClick={() => setSubmitted(false)} className="text-orange-600 font-semibold hover:text-orange-700 transition-colors" data-id="element-10">
              Submit another response
            </button>
          </div>
        </div>
      </motion.div>;
  }
  return <div className="max-w-3xl mx-auto px-6 py-12" data-id="element-11">
      <div className="text-center mb-12" data-id="element-12">
        <img src="https://cdn.magicpatterns.com/uploads/wXs3kcS5shAG1L2RbqJBMK/AVY_LOGO.png" alt="AVY Logo" className="h-24 mx-auto mb-8" data-id="element-13" />
        <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-14">
          Workshop Review
        </h1>
        <p className="text-lg text-gray-600 max-w-xl mx-auto leading-relaxed" data-id="element-15">
          Thank you for coming today. I'd love your honest feedback. These short
          questions help me improve future workshops.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-10" data-id="element-16">
        {/* Contact Info */}
        <section className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 space-y-6" data-id="element-17">
          <div data-id="element-18">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-19">
              Full Name *
            </label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Your name" className="w-full p-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all text-gray-700" required data-id="element-20" />
          </div>
          <div data-id="element-21">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-22">
              Company Name *
            </label>
            <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} placeholder="Your company" className="w-full p-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all text-gray-700" required data-id="element-23" />
          </div>
          <div data-id="element-24">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-25">
              Email Address *
            </label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="your@email.com" className="w-full p-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all text-gray-700" required data-id="element-26" />
          </div>
        </section>

        {/* Question 1 */}
        <section className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-27">
          <label className="block text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3" data-id="element-28">
            <Star className="w-6 h-6 text-orange-500" data-id="element-29" />
            How useful was today's session for your business?
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-id="element-30">
            {['Not useful', 'Somewhat useful', 'Useful', 'Very useful', 'Extremely useful'].map(option => <label key={option} className={`
                  flex items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all
                  ${formData.usefulness === option ? 'border-orange-500 bg-orange-50 text-orange-700 shadow-sm' : 'border-gray-100 hover:border-orange-200 hover:bg-gray-50 text-gray-600'}
                `} data-id="element-31">
                <input type="radio" name="usefulness" value={option} onChange={handleChange} className="sr-only" required data-id="element-32" />
                <span className="font-medium" data-id="element-33">{option}</span>
              </label>)}
          </div>
        </section>

        {/* Question 2 */}
        <section className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-34">
          <label className="block text-xl font-semibold text-gray-900 mb-4 flex items-center gap-3" data-id="element-35">
            <MessageSquare className="w-6 h-6 text-orange-500" data-id="element-36" />
            What was the single most valuable part of today for you?
          </label>
          <textarea name="valuablePart" value={formData.valuablePart} onChange={handleChange} placeholder="Share your thoughts..." className="w-full p-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all min-h-[120px] text-gray-700" required data-id="element-37" />
        </section>

        {/* Question 3 */}
        <section className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-38">
          <label className="block text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3" data-id="element-39">
            <HelpCircle className="w-6 h-6 text-orange-500" data-id="element-40" />
            After this session, how clear do you feel about what your brand
            needs next?
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-41">
            {['Not clear', 'A bit clearer', 'Clear', 'Very clear'].map(option => <label key={option} className={`
                  flex items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all
                  ${formData.clarity === option ? 'border-orange-500 bg-orange-50 text-orange-700 shadow-sm' : 'border-gray-100 hover:border-orange-200 hover:bg-gray-50 text-gray-600'}
                `} data-id="element-42">
                  <input type="radio" name="clarity" value={option} onChange={handleChange} className="sr-only" required data-id="element-43" />
                  <span className="font-medium" data-id="element-44">{option}</span>
                </label>)}
          </div>
        </section>

        {/* Question 4 & 5 */}
        <section className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-45">
          <label className="block text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3" data-id="element-46">
            <ThumbsUp className="w-6 h-6 text-orange-500" data-id="element-47" />
            Would you recommend this workshop to another founder?
          </label>
          <div className="flex gap-4 mb-8" data-id="element-48">
            {['Yes', 'No'].map(option => <label key={option} className={`
                  flex-1 flex items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all
                  ${formData.recommend === option ? 'border-orange-500 bg-orange-50 text-orange-700 shadow-sm' : 'border-gray-100 hover:border-orange-200 hover:bg-gray-50 text-gray-600'}
                `} data-id="element-49">
                <input type="radio" name="recommend" value={option} onChange={handleChange} className="sr-only" required data-id="element-50" />
                <span className="font-medium" data-id="element-51">{option}</span>
              </label>)}
          </div>

          <AnimatePresence data-id="element-52">
            {formData.recommend === 'Yes' && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="overflow-hidden" data-id="element-53">
                <label className="block text-lg font-medium text-gray-700 mb-3" data-id="element-54">
                  If yes, what would you tell them about it?
                </label>
                <textarea name="recommendQuote" value={formData.recommendQuote} onChange={handleChange} placeholder="Your review quote..." className="w-full p-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all min-h-[100px] text-gray-700" data-id="element-55" />
              </motion.div>}
          </AnimatePresence>
        </section>

        {/* Question 6 */}
        <section className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-56">
          <label className="block text-xl font-semibold text-gray-900 mb-4 flex items-center gap-3" data-id="element-57">
            <MessageSquare className="w-6 h-6 text-orange-500" data-id="element-58" />
            What, if anything, would you improve or change?
          </label>
          <textarea name="improvement" value={formData.improvement} onChange={handleChange} placeholder="Any suggestions for improvement..." className="w-full p-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all min-h-[100px] text-gray-700" data-id="element-59" />
        </section>

        {/* Question 7 */}
        <section className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-60">
          <label className="block text-xl font-semibold text-gray-900 mb-6" data-id="element-61">
            Can I use your comments as a testimonial?
          </label>
          <div className="space-y-3" data-id="element-62">
            {['Yes, with my name and business', 'Yes, but anonymous', 'No'].map(option => <label key={option} className={`
                  flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all
                  ${formData.testimonialConsent === option ? 'border-orange-500 bg-orange-50 text-orange-700 shadow-sm' : 'border-gray-100 hover:border-orange-200 hover:bg-gray-50 text-gray-600'}
                `} data-id="element-63">
                  <input type="radio" name="testimonialConsent" value={option} onChange={handleChange} className="sr-only" required data-id="element-64" />
                  <div className={`w-5 h-5 rounded-full border-2 mr-4 flex items-center justify-center ${formData.testimonialConsent === option ? 'border-orange-500' : 'border-gray-300'}`} data-id="element-65">
                    {formData.testimonialConsent === option && <div className="w-2.5 h-2.5 bg-orange-500 rounded-full" data-id="element-66" />}
                  </div>
                  <span className="font-medium" data-id="element-67">{option}</span>
                </label>)}
          </div>
        </section>

        <div className="pt-6" data-id="element-68">
          <button type="submit" disabled={isSubmitting} className={`
              w-full py-5 rounded-2xl text-white font-bold text-xl shadow-lg transition-all flex items-center justify-center gap-3
              ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-700 hover:shadow-orange-200 active:scale-[0.98]'}
            `} data-id="element-69">
            {isSubmitting ? <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin" data-id="element-70" /> : <>
                Submit Feedback
                <Send className="w-6 h-6" data-id="element-71" />
              </>}
          </button>
          <p className="text-center text-gray-400 text-sm mt-6" data-id="element-72">
            Your responses are securely sent to our team.
          </p>
        </div>
      </form>
    </div>;
}